/**
 * App selectors that do not depend on current-user.slice.
 *
 * Keeping this module at its existing path avoids forcing consumers to move
 * when the app reducer implementation changes.
 */
import { type DefaultRootState } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectActiveAppKey,
  selectActiveCompanyKey,
  selectAppState,
} from '@bbkAdminRedux/app/app.slice';
import {
  type ActiveApp,
  type ActiveCompany,
  type ActiveUser,
} from '@bbkAdminRedux/app/reducers';
import { authStatusFromQuery } from '@bbkAdminRedux/rtkq/auth-status';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';

const selectAuthenticatedUser = (
  state: DefaultRootState
): ActiveUser | undefined => {
  const query = bbkApiSlice.endpoints.getCurrentUser.select()(state);
  return authStatusFromQuery(query) === 'authenticated'
    ? query.data
    : undefined;
};

const selectActiveCompany = createSelector(
  [selectAuthenticatedUser, selectActiveCompanyKey],
  (user, companyKey): ActiveCompany | undefined =>
    user?.companies.find((company) => company.internal_name === companyKey)
);

const selectActiveApp = createSelector(
  [selectActiveCompany, selectActiveAppKey],
  (company, appKey): ActiveApp | undefined =>
    company?.applications.find((app) => app.encoded_id === appKey)
);

// Scoped consumers have historically treated these values as initialized.
// Keep that public type contract while unscoped or no-longer-valid selections
// continue to resolve to undefined at runtime.
export const selectUser = (state: DefaultRootState) =>
  selectAuthenticatedUser(state)!;

export const selectCompany = (state: DefaultRootState) =>
  selectActiveCompany(state)!;

export const selectApp = (state: DefaultRootState) => selectActiveApp(state)!;

export const selectCompanyName = (state: DefaultRootState) =>
  selectCompany(state)?.name;

export const selectAppDisplayName = (state: DefaultRootState) =>
  selectApp(state)?.display_name;

export const selectCompanyInternalName = (state: DefaultRootState) =>
  selectCompany(state)?.internal_name ?? '_';

export const selectCompanyId = (state: DefaultRootState): number | string =>
  selectCompany(state)?.id ?? '_';

export const selectAppId = (state: DefaultRootState) =>
  selectApp(state)?.encoded_id ?? '_';

export { selectAppState };
